import { REQUEST_STATUS } from './constants';
import { PERMISSIONS } from '../../permission-system/constants/permissions';
import {
  canCreateJobFromRecruitmentRequest,
  canLaunchCampaignFromRecruitmentRequest,
  canResendRecruitmentRequestNotification,
  canWithdrawRecruitmentRequest,
  isRecruitmentRequestCampaignLaunched,
  isRecruitmentRequestJobCreated,
} from './recruitmentRequestMappers';

export const VIEW_TYPES = {
  APPROVER: 'approver',
  REQUESTER: 'requester',
};

/**
 * Single source of truth for "My Requests" (requester view) accessibility.
 * The requester view is only for users who cannot create jobs directly; users
 * with CREATE_UPDATE_JOB are routed to job creation instead. Both the route guard and
 * the sidebar entry must use this so their visibility never drifts apart.
 * @param {(permissionKey: string) => boolean} can
 */
export const canAccessMyRequests = (can) =>
  typeof can === 'function' ? !can(PERMISSIONS.CREATE_UPDATE_JOB) : true;

/** Sidebar section visibility per view — extend here for new view types. */
export const VIEW_CONFIG = {
  [VIEW_TYPES.APPROVER]: {
    allowApprovalActions: true,
    allowRequesterActions: false,
    allowWithdraw: false,
    showRejectionBanner: false,
    showAmendForm: false,
    showResubmitActions: false,
    showAuditLog: true,
    showReasonSection: true,
    showApprovalChain: true,
    showOutOfOfficeNotice: true,
    showRequesterOutcomeBanner: false,
  },
  [VIEW_TYPES.REQUESTER]: {
    allowApprovalActions: false,
    allowRequesterActions: true,
    allowWithdraw: true,
    showRejectionBanner: true,
    showAmendForm: true,
    showResubmitActions: true,
    showAuditLog: true,
    showReasonSection: true,
    showApprovalChain: true,
    showOutOfOfficeNotice: true,
    showRequesterOutcomeBanner: true,
  },
};

export const PAGE_CONFIG = {
  [VIEW_TYPES.APPROVER]: {
    viewBadge: 'Approver view',
    pageTitle: 'Approvals Inbox',
    pageDescription:
      'Review hire and campaign requests routed to you. Approve or reject with comments — approvers are notified automatically in sequential or parallel chains.',
    defaultStatusTab: 'awaiting_me',
    showNewRequest: false,
    showApprovalChainsButton: true,
    statCards: [
      { key: 'awaitingYourApproval', label: 'Awaiting your approval' },
      { key: 'pendingHire', label: 'Pending hire requests' },
      { key: 'pendingCampaign', label: 'Pending campaign requests' },
      { key: 'approvedThisMonth', label: 'Approved by you (30d)' },
    ],
    statusTabs: [
      { id: 'awaiting_me', label: 'Awaiting me' },
      { id: 'approved', label: 'Approved' },
      { id: 'rejected', label: 'Rejected' },
      { id: 'all', label: 'All in scope' },
    ],
    requestsHeading: 'Requests',
  },
  [VIEW_TYPES.REQUESTER]: {
    viewBadge: 'Requester view',
    pageTitle: 'My Hiring Requests',
    pageDescription:
      'Track hire and campaign requests you have submitted. View approval progress, respond to feedback, and resubmit when needed.',
    defaultStatusTab: 'all',
    showNewRequest: true,
    showApprovalChainsButton: true,
    statCards: [
      { key: 'myOpenRequests', label: 'My open requests' },
      { key: 'myApproved', label: 'My approved' },
      { key: 'needsAmendment', label: 'Needs amendment' },
    ],
    statusTabs: [
      { id: 'all', label: 'All my requests' },
      { id: 'in_progress', label: 'In progress' },
      { id: 'approved', label: 'Approved' },
      { id: 'needs_amendment', label: 'Needs amendment' },
    ],
    requestsHeading: 'My requests',
  },
};

export const getViewConfig = (viewType) =>
  VIEW_CONFIG[viewType] || VIEW_CONFIG[VIEW_TYPES.APPROVER];

export const getPageConfig = (viewType) =>
  PAGE_CONFIG[viewType] || PAGE_CONFIG[VIEW_TYPES.APPROVER];

export const isCurrentUserAwaitingOnRequest = (request, userId) =>
  Boolean(userId) &&
  request?.status === REQUEST_STATUS.PENDING &&
  request?.approvers?.some(
    (a) =>
      a.status === 'awaiting' &&
      (a.userId === userId || a.accountId === userId),
  );

export const isCurrentUserRequester = (request, userId) =>
  Boolean(userId) && request?.requestedBy?.id === userId;

/**
 * Merges static VIEW_CONFIG with request-specific flags for sidebar rendering.
 */
export const getApprovalRequestSidebarFlags = (request, viewType, options = {}) => {
  const { amendMode = false, currentUserId } = options;
  const base = getViewConfig(viewType);
  const isRequesterView = viewType === VIEW_TYPES.REQUESTER;
  const isApproverView = viewType === VIEW_TYPES.APPROVER;
  const isOwner = isCurrentUserRequester(request, currentUserId);
  const isAwaitingMe = isCurrentUserAwaitingOnRequest(request, currentUserId);
  const isRejected = request?.status === REQUEST_STATUS.REJECTED;
  const isPending = request?.status === REQUEST_STATUS.PENDING;
  const isApproved = request?.status === REQUEST_STATUS.APPROVED;
  return {
    ...base,
    showApprovalActions:
      base.allowApprovalActions && isApproverView && isAwaitingMe && isPending,
    showWithdraw:
      base.allowWithdraw &&
      isRequesterView &&
      isOwner &&
      isPending &&
      canWithdrawRecruitmentRequest(request),
    showRejectionBanner:
      base.showRejectionBanner && isRequesterView && isOwner && isRejected && !amendMode,
    showAmendForm: base.showAmendForm && isRequesterView && isOwner && isRejected && amendMode,
    showResubmitActions:
      base.showResubmitActions && isRequesterView && isOwner && isRejected && !amendMode,
    showRequesterOutcomeBanner:
      base.showRequesterOutcomeBanner &&
      isRequesterView &&
      isOwner &&
      (isApproved || isRejected) &&
      !isRecruitmentRequestJobCreated(request) &&
      !isRecruitmentRequestCampaignLaunched(request),
    showCreateJobAction:
      isRequesterView &&
      isOwner &&
      canCreateJobFromRecruitmentRequest(request),
    showLaunchCampaignAction:
      isRequesterView &&
      isOwner &&
      canLaunchCampaignFromRecruitmentRequest(request),
    showJobCreatedBanner: isRecruitmentRequestJobCreated(request),
    showCampaignLaunchedBanner: isRecruitmentRequestCampaignLaunched(request),
    showResendNotification:
      isRequesterView &&
      isOwner &&
      isPending &&
      canResendRecruitmentRequestNotification(request),
    showApprovalChain: base.showApprovalChain,
    showReasonSection: base.showReasonSection,
    showAuditLog: base.showAuditLog,
    showOutOfOfficeNotice: base.showOutOfOfficeNotice && Boolean(request?.outOfOfficeNotice),
  };
};
